// ============================================================================
// PARTS4DRIVE - ONBOARDING SERVICE
// Handles vendor and marketplace onboarding flow
// ============================================================================

import { authClient } from './auth';

const ONBOARDING_SESSION_KEY = 'p4d_onboarding_session';

export interface OnboardingOrgData {
  email?: string;
  displayName?: string;
  role?: string;
  name?: string;
  industry?: string;
  currency?: string;
  countryCode?: string;
  address?: string;
  phone?: string;
  associatedDepotHub?: string | null;
  operatingState?: string | null;
  depotHub?: string | null;
  state?: string | null;
  plan?: string;
  provider?: string;
  teamMembers?: Array<{ name: string; email: string; role: string }>;
  [key: string]: any;
}

export class OnboardingService {
  currentStep: number;
  userData: any;
  orgData: OnboardingOrgData;

  constructor() {
    this.currentStep = 1;
    this.userData = null;
    this.orgData = {};
  }

  async selectRole(role: string) {
    this.currentStep = 1;
    this.orgData.role = role;
    this.saveSession();
    return { step: this.currentStep, role };
  }

  async createOrganizationStep(orgDetails: Partial<OnboardingOrgData>) {
    this.currentStep = 2;
    this.orgData = {
      ...this.orgData,
      ...orgDetails,
    };
    this.saveSession();
    return { step: this.currentStep, orgData: this.orgData };
  }

  async addTeamMembers(members: Array<{ name: string; email: string; role: string }>) {
    this.currentStep = 3;
    this.orgData.teamMembers = members;
    this.saveSession();
    return { step: this.currentStep, members };
  }

  async completeOnboarding(userId?: string) {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(ONBOARDING_SESSION_KEY);
      }

      const slug = (this.orgData.name || 'store').toLowerCase().replace(/[^a-z0-9]+/g, '-');
      let sellerId = 'sel_' + Date.now();

      try {
        const response = await fetch('/api/seller/onboarding', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            storeName: this.orgData.name,
            storeSlug: slug,
            whatsappNumber: this.orgData.phone || '+2348000000000',
            shopLocation: this.orgData.address || (this.orgData.countryCode === 'GH' ? 'Accra, Ghana' : 'Lagos, Nigeria'),
            country: this.orgData.countryCode || (this.orgData.currency === 'GHS' ? 'GH' : 'NG'),
            currency: this.orgData.currency || (this.orgData.countryCode === 'GH' ? 'GHS' : 'NGN'),
            associatedDepotHub: this.orgData.associatedDepotHub || this.orgData.depotHub || null,
            operatingState: this.orgData.operatingState || this.orgData.state || null,
            plan: this.orgData.plan || 'starter',
          }),
          credentials: 'include',
        });
        const resData = await response.json();
        if (resData?.data?.sellerId) {
          sellerId = resData.data.sellerId;
        }
      } catch (apiErr) {
        console.warn('Seller API onboarding warning:', apiErr);
      }

      // Check if user joined via a team invite link
      const pendingInviteStr = typeof localStorage !== 'undefined' ? localStorage.getItem('p4d_pending_invite') : null;
      if (pendingInviteStr) {
        try {
          const invite = JSON.parse(pendingInviteStr);
          if (invite?.orgId) {
            sellerId = invite.orgId;
            localStorage.setItem('p4d_seller_id', invite.orgId);
            localStorage.setItem('p4d_member_role', invite.role || 'CASHIER');
            localStorage.removeItem('p4d_pending_invite');
          }
        } catch (inviteErr) {
          console.warn('Invite linkage warning:', inviteErr);
        }
      }

      return {
        success: true,
        orgId: sellerId,
        redirect: '/dashboard',
      };
    } catch (error) {
      console.error('Onboarding completion error:', error);
      throw error;
    }
  }

  saveSession() {
    if (typeof localStorage === 'undefined') return;
    const session = {
      step: this.currentStep,
      ...this.orgData,
    };
    try {
      localStorage.setItem(ONBOARDING_SESSION_KEY, JSON.stringify(session));
    } catch {}
  }

  loadSession() {
    if (typeof localStorage === 'undefined') return null;
    try {
      const data = localStorage.getItem(ONBOARDING_SESSION_KEY);
      if (data) {
        const session = JSON.parse(data);
        this.currentStep = session.step || 1;
        this.orgData = session;
        return session;
      }
    } catch {}
    return null;
  }
}

let _onboardingService: OnboardingService | null = null;

export const getOnboardingService = (): OnboardingService => {
  if (!_onboardingService) {
    _onboardingService = new OnboardingService();
  }
  return _onboardingService;
};
